import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { apiAuth } from '@/api/auth-client';
import { OG_AUTH_CHANGED_EVENT } from '@/api/opengater/tokens';
import { panelBearer } from './session';
import { PANEL_AUTH_CHANGED_EVENT } from './tokens';
import { AUTHORIZATION_DENIED_EVENT, authorizationSource, } from './authorizationEvents';
import { normalizeRole } from './roles';
const ROLE_POLL_MS = 60_000;
const CurrentUserContext = createContext(null);
export function CurrentUserProvider({ children }) {
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState(() => panelBearer() ? 'loading' : 'idle');
    const request = useRef(0);
    const refetch = useCallback(async () => {
        const bearer = panelBearer();
        if (!bearer) {
            request.current += 1;
            setUser(null);
            setStatus('idle');
            return;
        }
        const current = ++request.current;
        setStatus((previous) => (previous === 'ready' ? previous : 'loading'));
        try {
            const raw = await apiAuth('/users/me');
            if (current !== request.current)
                return;
            setUser({ ...raw, role: normalizeRole(raw.role) });
            setStatus('ready');
        }
        catch {
            if (current !== request.current)
                return;
            setUser(null);
            setStatus('error');
        }
    }, []);
    useEffect(() => {
        void refetch();
        const timer = window.setInterval(() => void refetch(), ROLE_POLL_MS);
        const onAuthorizationDenied = (event) => {
            if (authorizationSource(event) === 'panel')
                void refetch();
        };
        const onSessionChanged = () => void refetch();
        const onStorage = (event) => {
            if (event.key === null ||
                event.key === 'access_token' ||
                event.key === 'miniapp_access_token' ||
                event.key === 'og_access_token') {
                void refetch();
            }
        };
        window.addEventListener(AUTHORIZATION_DENIED_EVENT, onAuthorizationDenied);
        window.addEventListener(PANEL_AUTH_CHANGED_EVENT, onSessionChanged);
        window.addEventListener(OG_AUTH_CHANGED_EVENT, onSessionChanged);
        window.addEventListener('storage', onStorage);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener(AUTHORIZATION_DENIED_EVENT, onAuthorizationDenied);
            window.removeEventListener(PANEL_AUTH_CHANGED_EVENT, onSessionChanged);
            window.removeEventListener(OG_AUTH_CHANGED_EVENT, onSessionChanged);
            window.removeEventListener('storage', onStorage);
        };
    }, [refetch]);
    const value = useMemo(() => ({ user, role: user?.role ?? null, status, refetch }), [user, status, refetch]);
    return _jsx(CurrentUserContext.Provider, { value: value, children: children });
}
export function useCurrentUser() {
    const value = useContext(CurrentUserContext);
    if (!value)
        throw new Error('useCurrentUser must be used within <CurrentUserProvider>');
    return value;
}
